import { TimeService, getMomentTz } from "@dexteel/mesf-core";
import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
	UTILITY_CONSUMPTION_WINDOW,
	type UtilityConsumptionMeterGap,
	type UtilityConsumptionMillRolling,
	type UtilityConsumptionSummary,
} from "../../../shared/consumption/models/UtilityConsumption";
import {
	formatCostPerKiloGallonValue,
	formatCurrency,
	formatVolume,
	KILO_VOLUME_UNIT,
	RATIO_UNIT,
	VOLUME_UNIT,
} from "../../../shared/consumption/utils/format";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_UNIT_COLOR,
} from "../../../shared/consumption/utils/tokens";
import { getMeterGapNote } from "../../utils/meterGaps";
import { ConsumptionMetric } from "../ConsumptionMetric";
import { ConsumptionCard, metricRowSx } from "./ConsumptionCard";

const moment = getMomentTz();
const APPROXIMATE_HINT =
	"The bill behind this price does not cover all of it, so the rest is priced at that same rate.";

export const formatRollingInstant = (utc: Date | null) =>
	utc === null ? null : moment(utc).format("MMM D, HH:mm");

export const formatRollingWindow = (
	startUtc: Date | null,
	endUtc: Date | null,
) => {
	if (startUtc === null || endUtc === null) return null;
	const start = moment(startUtc);
	const end = moment(endUtc);
	return `${start.format("MMM D, HH:mm")} - ${end.format(
		end.isSame(start, "day") ? "HH:mm" : "MMM D, HH:mm",
	)}`;
};

export const formatBillPeriod = (start: Date | null, end: Date | null) =>
	start === null || end === null
		? null
		: `${moment.utc(start).format("MMM D")} - ${moment
				.utc(end)
				.format("MMM D, YYYY")}`;

export const formatCostPerTon = (
	costPerTon: number | null,
	isApproximate: boolean | null,
) => {
	const cost = formatCurrency(costPerTon);
	return cost === null ? null : `${isApproximate ? "≈" : ""}${cost}/ton`;
};

export const renderCostLine = ({
	costPerTon,
	isApproximate,
	trailing,
}: {
	costPerTon: number | null;
	isApproximate: boolean | null;
	trailing: string | null;
}): ReactNode => {
	const cost = formatCostPerTon(costPerTon, isApproximate);
	if (cost === null) return trailing;

	return (
		<Box
			component="span"
			sx={{
				alignItems: "baseline",
				display: "inline-flex",
				flexWrap: "wrap",
				gap: 0.25,
				minWidth: 0,
			}}
		>
			<Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
				{cost}
			</Box>
			{trailing !== null && (
				<Box component="span" sx={{ color: CONSUMPTION_UNIT_COLOR, pl: 0.25 }}>
					· {trailing}
				</Box>
			)}
		</Box>
	);
};

interface RollingEfficiencyCardProps {
	summary: UtilityConsumptionSummary;
	meterGaps: UtilityConsumptionMeterGap[];
	millRolling: UtilityConsumptionMillRolling[];
	showCost: boolean;
}

const PeriodCostCard = ({
	summary,
	showCost,
}: {
	summary: UtilityConsumptionSummary;
	showCost: boolean;
}) => {
	const billPeriod = formatBillPeriod(
		summary.PeriodCostRateBillStart,
		summary.PeriodCostRateBillEnd,
	);
	const isApproximate = Boolean(summary.IsPeriodCostRateApproximate);
	const hasRate = summary.PeriodCostRatePerGallon !== null;

	const title = !showCost
		? "Consumption for this period"
		: !hasRate
			? "No bill entered for these accounts yet"
			: `${isApproximate ? "≈ " : ""}Priced by the bill of ${billPeriod}`;

	return (
		<ConsumptionCard
			area="efficiency"
			label="Consumption and cost for the period on screen"
			title={title}
			upper={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="This period"
						value={formatVolume(summary.MainTotalGallons)}
						unit={VOLUME_UNIT}
						secondary={summary.IsMainPartial ? "Period still open" : null}
						tooltip={`Water this target consumed over the period on screen, plant time (${TimeService.getInstance().getServerTimeZone()}).`}
					/>
					{showCost && (
						<ConsumptionMetric
							label="Cost of this period"
							value={formatCurrency(summary.MainTotalCost)}
							secondary={hasRate && isApproximate ? "Approximate" : null}
							tooltip={
								hasRate
									? `Those gallons at ${formatCostPerKiloGallonValue(
											summary.PeriodCostRatePerGallon,
										)}/${KILO_VOLUME_UNIT}.${
											isApproximate ? ` ${APPROXIMATE_HINT}` : ""
										}`
									: "No bill covers this period, and no earlier one is available to price it with."
							}
						/>
					)}
				</Box>
			}
			lower={
				!showCost ? null : (
					<Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
						<Box sx={metricRowSx}>
							<ConsumptionMetric
								label="Cost per 1000 Gallons"
								value={formatCostPerKiloGallonValue(
									summary.PeriodCostRatePerGallon,
								)}
								unit={`/${KILO_VOLUME_UNIT}`}
								tooltip={
									!hasRate
										? "No bill covers this period, and no earlier one is available to price it with."
										: isApproximate
											? `What 1000 gallons cost. ${APPROXIMATE_HINT}`
											: `What 1000 gallons cost, from the bill of ${billPeriod}.`
								}
							/>
						</Box>
						{hasRate && isApproximate && (
							<Typography
								component="div"
								sx={{ ...CONSUMPTION_CAPTION_SX, color: "#767F89" }}
							>
								{`Approximate: the bill of ${billPeriod} does not cover the whole period on screen, so the rest of it is priced at that same rate.`}
							</Typography>
						)}
					</Box>
				)
			}
		/>
	);
};

export const RollingEfficiencyCard = ({
	summary,
	meterGaps,
	millRolling,
	showCost,
}: RollingEfficiencyCardProps) => {
	if (millRolling.length === 0) {
		return <PeriodCostCard summary={summary} showCost={showCost} />;
	}

	const billPeriod = formatBillPeriod(
		summary.CostRateBillStart,
		summary.CostRateBillEnd,
	);

	const currentRollingGap = getMeterGapNote(
		meterGaps,
		UTILITY_CONSUMPTION_WINDOW.CURRENT_ROLLING,
	);
	const currentRollingStartUtc = millRolling[0]?.CurrentRollingStartUtc ?? null;
	const currentRollingWindow = formatRollingWindow(
		currentRollingStartUtc,
		summary.DataThroughUtc,
	);
	const isCurrentRollingUnmeasured =
		currentRollingStartUtc !== null &&
		summary.DataThroughUtc !== null &&
		currentRollingStartUtc.getTime() >= summary.DataThroughUtc.getTime();

	const costNote = isCurrentRollingUnmeasured
		? null
		: summary.CostRatePerGallon === null
			? "No bill entered for this account yet."
			: summary.IsCostRateApproximate
				? `Approximate: the bill of ${billPeriod} does not cover the whole run, so the rest of it is priced at that same rate.`
				: null;

	return (
		<ConsumptionCard
			area="efficiency"
			label="Rolling consumption efficiency"
			title={`Section · ${summary.CurrentRollingSection ?? "N/A"}`}
			upper={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="This rolling"
						value={formatVolume(summary.CurrentRollingGallonsPerTon)}
						unit={RATIO_UNIT}
						secondary={
							currentRollingGap !== null
								? currentRollingGap.secondary
								: renderCostLine({
										costPerTon: summary.CurrentRollingCostPerTon,
										isApproximate: summary.IsCostRateApproximate,
										trailing: isCurrentRollingUnmeasured
											? `Started ${formatRollingInstant(currentRollingStartUtc)}`
											: null,
									})
						}
						tooltip={
							currentRollingGap !== null
								? currentRollingGap.tooltip
								: isCurrentRollingUnmeasured
									? `This run started ${formatRollingInstant(currentRollingStartUtc)}, and every meter of this target has only reported through ${formatRollingInstant(summary.DataThroughUtc)}. Nothing has been measured for it yet.`
									: `Gallons per ton produced during the current run of this section${
											currentRollingWindow === null
												? ""
												: `, ${currentRollingWindow}`
										}.${
											summary.IsCostRateApproximate
												? ` The bill of ${billPeriod} does not cover the whole run; the rest of it is priced at that same rate.`
												: ""
										}`
						}
					/>
					<ConsumptionMetric
						label="Previous rolling"
						value={formatVolume(summary.PreviousRollingGallonsPerTon)}
						unit={RATIO_UNIT}
						secondary={renderCostLine({
							costPerTon: summary.PreviousRollingCostPerTon,
							isApproximate: summary.IsPreviousCostRateApproximate,
							trailing: formatRollingWindow(
								summary.PreviousRollingStartUtc,
								summary.PreviousRollingEndUtc,
							),
						})}
						tooltip={
							summary.PreviousRollingSection === null
								? "No earlier completed run of this section was found."
								: `Gallons per ton produced the last time this section was rolled.${
										summary.IsPreviousCostRateApproximate
											? ` ${APPROXIMATE_HINT}`
											: ""
									}`
						}
					/>
				</Box>
			}
			lower={
				!showCost ? null : (
					<Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
						<Box sx={metricRowSx}>
							<ConsumptionMetric
								label="Cost per 1000 Gallons"
								value={formatCostPerKiloGallonValue(summary.CostRatePerGallon)}
								unit={`/${KILO_VOLUME_UNIT}`}
								tooltip={
									summary.CostRatePerGallon === null
										? "No bill entered for this account yet."
										: summary.IsCostRateApproximate
											? `What 1000 gallons cost. ${APPROXIMATE_HINT}`
											: `What 1000 gallons cost, from the bill of ${billPeriod}.`
								}
							/>
						</Box>
						{costNote !== null && (
							<Typography
								component="div"
								sx={{ ...CONSUMPTION_CAPTION_SX, color: "#767F89" }}
							>
								{costNote}
							</Typography>
						)}
					</Box>
				)
			}
		/>
	);
};
